const UNKNOWN_LOCATION_VALUES = new Set([
  '',
  '(not set)',
  'not set',
  'unknown',
  'unassigned',
  '(unknown)',
  'undefined',
  'null',
]);

const COUNTRY_NAME_CODES = {
  'united states': 'us',
  'united states of america': 'us',
  canada: 'ca',
  mexico: 'mx',
  'united kingdom': 'gb',
  'great britain': 'gb',
  england: 'gb',
  france: 'fr',
  germany: 'de',
  spain: 'es',
  italy: 'it',
  ireland: 'ie',
  netherlands: 'nl',
  belgium: 'be',
  switzerland: 'ch',
  austria: 'at',
  portugal: 'pt',
  australia: 'au',
  'new zealand': 'nz',
  india: 'in',
  china: 'cn',
  japan: 'jp',
  brazil: 'br',
  argentina: 'ar',
  colombia: 'co',
  'south africa': 'za',
};

const US_STATE_NAMES = new Set('alabama,alaska,arizona,arkansas,california,colorado,connecticut,delaware,district of columbia,florida,georgia,hawaii,idaho,illinois,indiana,iowa,kansas,kentucky,louisiana,maine,maryland,massachusetts,michigan,minnesota,mississippi,missouri,montana,nebraska,nevada,new hampshire,new jersey,new mexico,new york,north carolina,north dakota,ohio,oklahoma,oregon,pennsylvania,rhode island,south carolina,south dakota,tennessee,texas,utah,vermont,virginia,washington,west virginia,wisconsin,wyoming'.split(','));

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const toCoordinate = (value, min, max) => {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= min && number <= max ? number : null;
};

export const GA4_LOCATION_METRICS = [
  { key: 'users', label: 'Users', format: 'number' },
  { key: 'eventCount', label: 'Events', format: 'number' },
  { key: 'impressions', label: 'Impressions', format: 'number' },
  { key: 'clicks', label: 'Clicks', format: 'number' },
  { key: 'cost', label: 'Spend', format: 'currency' },
  { key: 'newUsers', label: 'New users', format: 'number' },
  { key: 'sessions', label: 'Sessions', format: 'number' },
  { key: 'engagedSessions', label: 'Engaged sessions', format: 'number' },
  { key: 'engagementRate', label: 'Engagement rate', format: 'percent' },
  { key: 'conversions', label: 'Key events / conversions', format: 'number' },
  { key: 'revenue', label: 'Revenue', format: 'currency' },
  { key: 'donationValue', label: 'Donation value', format: 'currency' },
];

export const isUnknownLocation = (value) => UNKNOWN_LOCATION_VALUES.has(String(value ?? '').trim().toLowerCase());

export const normalizeCountryCode = (value, countryName = '') => {
  const code = String(value ?? '').trim().toLowerCase();
  if (/^[a-z]{2}$/.test(code)) return code;
  return COUNTRY_NAME_CODES[String(countryName ?? '').trim().toLowerCase()] || '';
};

export const normalizeLocationRow = (row = {}) => {
  const source = { ...(row.raw || {}), ...row };
  const level = String(source.level ?? source.LEVEL ?? '').trim().toLowerCase();
  const label = String(source.label ?? source.LOCATION ?? source.name ?? '').trim();
  let country = String(source.country ?? source.COUNTRY ?? '').trim();
  let region = String(source.region ?? source.state ?? source.REGION ?? source.STATE ?? '').trim();
  let city = String(source.city ?? source.CITY ?? '').trim();
  if (!country && level.includes('country')) country = label;
  if (!region && (level.includes('region') || level.includes('state') || level.includes('province'))) region = label;
  if (!city && (level.includes('city') || level.includes('metro') || level.includes('dma'))) city = label;
  let countryCode = normalizeCountryCode(source.countryCode ?? source.COUNTRY_CODE ?? source.countryId ?? source.country_id ?? source.code, country);
  if (!country && countryCode && typeof Intl.DisplayNames === 'function') {
    try { country = new Intl.DisplayNames(['en'], { type: 'region' }).of(countryCode.toUpperCase()) || ''; } catch { country = ''; }
  }
  if (!countryCode && US_STATE_NAMES.has(region.toLowerCase())) {
    countryCode = 'us';
    country = 'United States';
  }
  const unknown = isUnknownLocation(country) || !countryCode;

  return {
    country: isUnknownLocation(country) ? 'Unknown' : country,
    countryCode,
    region: isUnknownLocation(region) ? '' : region,
    city: isUnknownLocation(city) ? '' : city,
    eventCount: toFiniteNumber(row.eventCount),
    users: toFiniteNumber(row.users ?? row.activeUsers ?? row.count ?? row.value),
    impressions: toFiniteNumber(row.impressions ?? row.IMPRESSIONS),
    clicks: toFiniteNumber(row.clicks ?? row.CLICKS),
    cost: toFiniteNumber(row.cost ?? row.COST),
    newUsers: toFiniteNumber(row.newUsers),
    sessions: toFiniteNumber(row.sessions),
    engagedSessions: toFiniteNumber(row.engagedSessions),
    engagementRate: toFiniteNumber(row.engagementRate),
    conversions: toFiniteNumber(row.conversions ?? row.keyEvents),
    revenue: toFiniteNumber(row.revenue ?? row.totalRevenue),
    donationValue: toFiniteNumber(row.donationValue),
    latitude: toCoordinate(row.latitude ?? row.lat, -90, 90),
    longitude: toCoordinate(row.longitude ?? row.lng ?? row.lon, -180, 180),
    coordinateMatch: String(row.coordinateMatch || ''),
    geonameId: row.geonameId ? Number(row.geonameId) : null,
    unknown,
  };
};

const addMetrics = (target, row) => {
  target.eventCount += row.eventCount;
  target.users += row.users;
  target.impressions += row.impressions;
  target.clicks += row.clicks;
  target.cost += row.cost;
  target.newUsers += row.newUsers;
  target.sessions += row.sessions;
  target.engagedSessions += row.engagedSessions;
  target.conversions += row.conversions;
  target.revenue += row.revenue;
  target.donationValue += row.donationValue;
  target.engagementRate = target.sessions > 0
    ? (target.engagedSessions / target.sessions) * 100
    : Math.max(target.engagementRate, row.engagementRate);
};

const aggregateBy = (rows, getKey, getLabels) => {
  const groups = new Map();

  (Array.isArray(rows) ? rows : []).forEach((input) => {
    const row = normalizeLocationRow(input);
    if (row.unknown) return;
    const key = getKey(row);
    if (!key) return;
    if (!groups.has(key)) groups.set(key, { ...row, ...getLabels(row) });
    else addMetrics(groups.get(key), row);
  });

  return [...groups.values()].sort((a, b) => b.users - a.users);
};

export const aggregateRegionRows = (rows = [], countryCode = '') => {
  const normalizedCode = normalizeCountryCode(countryCode);
  return aggregateBy(
    rows.filter((row) => normalizeLocationRow(row).countryCode === normalizedCode),
    (row) => row.region.toLowerCase(),
    (row) => ({ city: '' }),
  );
};

export const aggregateCityRows = (rows = [], countryCode = '', region = '') => {
  const normalizedCode = normalizeCountryCode(countryCode);
  const normalizedRegion = String(region || '').trim().toLowerCase();
  return aggregateBy(
    rows.filter((row) => {
      const normalized = normalizeLocationRow(row);
      return normalized.countryCode === normalizedCode
        && normalized.region.toLowerCase() === normalizedRegion;
    }),
    (row) => row.city.toLowerCase(),
    (row) => row,
  );
};

export const aggregateLocationRows = (rows = []) => {
  const detail = new Map();
  const countries = new Map();
  const unknown = {
    country: 'Unknown / unmapped', countryCode: '', region: '', city: '', unknown: true,
    eventCount: 0, users: 0, impressions: 0, clicks: 0, cost: 0, newUsers: 0, sessions: 0, engagedSessions: 0, engagementRate: 0,
    conversions: 0, revenue: 0, donationValue: 0,
  };

  (Array.isArray(rows) ? rows : []).forEach((input) => {
    const row = normalizeLocationRow(input);
    if (row.unknown) {
      addMetrics(unknown, row);
      return;
    }

    const detailKey = [row.countryCode, row.region.toLowerCase(), row.city.toLowerCase()].join('|');
    if (!detail.has(detailKey)) detail.set(detailKey, { ...row });
    else addMetrics(detail.get(detailKey), row);

    if (!countries.has(row.countryCode)) {
      countries.set(row.countryCode, {
        ...row,
        region: '',
        city: '',
      });
    } else {
      addMetrics(countries.get(row.countryCode), row);
    }
  });

  return {
    detailRows: [...detail.values()].sort((a, b) => b.users - a.users),
    countryRows: [...countries.values()].sort((a, b) => b.users - a.users),
    unknown,
    hasUnknown: GA4_LOCATION_METRICS.some(({ key }) => unknown[key] > 0),
  };
};

export const getAvailableLocationMetrics = (rows = []) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const available = GA4_LOCATION_METRICS.filter((metric) => safeRows.some((row) => toFiniteNumber(row?.[metric.key]) > 0));
  return available.length ? available : [GA4_LOCATION_METRICS[0]];
};

export const formatLocationMetric = (value, format = 'number') => {
  const safeValue = toFiniteNumber(value);
  if (format === 'currency') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(safeValue);
  }
  if (format === 'percent') return `${safeValue.toFixed(1)}%`;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(safeValue);
};
